import { useMutation } from "@tanstack/react-query";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { TemplateCollection } from "PosterEditor/model/template-collection";

export function useCollectionRemoteStore() {
  const { client, userID } = useAuthContext();

  const save = useMutation({
    mutationFn: async (collection: TemplateCollection) => {
      if (!client) {
        throw new Error("Client not initialized");
      }
      if (!userID)
        throw new Error("useCollectionRemoteStore 13: User not authenticated");

      const { id, name, createdAt, updatedAt, description } = collection;
      try {
        const { data, error } = await client.from("collections").upsert(
          [
            {
              id,
              created_by: userID,
              name: name,
              created_at: new Date(createdAt).toISOString(),
              updated_at: new Date(updatedAt).toISOString(),
              description: description,
            },
          ],
          { onConflict: "id" }
        );

        if (error) {
          throw error;
        }

        if (collection.templateIDs && collection.templateIDs.length > 0) {
          const templateRelations = collection.templateIDs.map(
            (templateID, index) => ({
              collection_id: id,
              template_id: templateID,
              template_index: index,
            })
          );

          const { error: relationError } = await client.rpc(
            "replace_collection_templates",
            {
              p_collection_id: id,
              p_templates: templateRelations,
            }
          );

          if (relationError) {
            throw relationError;
          }
        }

        return data;
      } catch (err) {
        throw err;
      }
    },
  });

  const remove = useMutation({
    mutationFn: async (collectionID: string) => {
      if (!client) {
        throw new Error("Client not initialized");
      }
      if (!userID)
        throw new Error("useCollectionRemoteStore 69: User not authenticated");

      const { data, error } = await client
        .from("collections")
        .delete()
        .eq("id", collectionID);
      if (error) throw error;
      return data;
    },
  });

  const fetchAllCollections = async (): Promise<TemplateCollection[]> => {
    if (!client) {
      throw new Error("Client not initialized");
    }

    const { data, error } = await client
      .from("collections")
      .select("*, collection_templates(template_id, template_index)");
    if (error) throw error;
    const collections: TemplateCollection[] = data.map((collection) => {
      const sortedTemplates = (
        collection.collection_templates as {
          template_id: string;
          template_index: number;
        }[]
      )
        ?.sort((a, b) => a.template_index - b.template_index)
        .map((ct) => ct.template_id);

      return {
        id: collection.id,
        name: collection.name,
        createdByID: collection.created_by,
        description: collection.description,
        createdAt: new Date(collection.created_at),
        updatedAt: new Date(collection.updated_at),
        templateIDs: sortedTemplates || [],
      };
    });

    return collections;
  };

  const fetchCollectionByID = async (
    collectionID: string
  ): Promise<TemplateCollection | undefined> => {
    return fetchCollectionsByIDs([collectionID]).then((collections) => {
      if (collections.length > 0) {
        return collections[0];
      }
      return undefined;
    });
  };

  const fetchCollectionsByIDs = async (
    collectionIDs: string[]
  ): Promise<TemplateCollection[]> => {
    if (!client) {
      throw new Error("Client not initialized");
    }
    const { data, error } = await client
      .from("collections")
      .select("*, collection_templates(template_id, template_index)")
      .in("id", collectionIDs);
    if (error) throw error;
    if (!data) {
      throw new Error(
        `Collections with IDs ${collectionIDs.join(", ")} not found`
      );
    }

    const collectionsUnsorted = data.map((collection) => {
      const sortedTemplates = (
        collection.collection_templates as {
          template_id: string;
          template_index: number;
        }[]
      )
        ?.sort((a, b) => a.template_index - b.template_index)
        .map((ct) => ct.template_id);

      return {
        id: collection.id,
        name: collection.name,
        createdByID: collection.created_by,
        description: collection.description,
        createdAt: new Date(collection.created_at),
        updatedAt: new Date(collection.updated_at),
        templateIDs: sortedTemplates || [],
      };
    });

    const collections = collectionIDs.map(
      (id) => collectionsUnsorted.find((c) => c.id === id)!
    );

    return collections;
  };

  return {
    save,
    remove,
    fetchAllCollections,
    fetchCollectionByID,
    fetchCollectionsByIDs,
  };
}
